// src/authProvider.ts
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  DocumentData,
} from 'firebase/firestore';
import { FirebaseAuthProvider, FirebaseDataProvider } from 'react-admin-firebase';
import type { RAFirebaseOptions } from 'react-admin-firebase';
import { firebaseConfig, db, auth } from './firebase';
import type {
  GetListParams,
  UpdateParams,
  RaRecord,
  Identifier,
  GetListResult,
} from 'react-admin';
import {
  ensureAdminSession,
  isPermissionDeniedError,
  withAdminSessionRetry,
  ensureAdminSessionEventually,
} from './adminSession';

const options: RAFirebaseOptions = {
  logging: true,
  persistence: 'local',
  // no watch for now
};

const baseAuthProvider = FirebaseAuthProvider(firebaseConfig, options);
const baseDataProvider = FirebaseDataProvider(firebaseConfig, options);

const getPagedDocs = async (
  resource: string,
  sortField: string,
  direction: 'asc' | 'desc',
  page: number,
  perPage: number
) => {
  let baseQuery = query(
    collection(db, resource),
    orderBy(sortField, direction),
    limit(perPage)
  );

  if (page > 1) {
    const offsetQuery = query(
      collection(db, resource),
      orderBy(sortField, direction),
      limit((page - 1) * perPage)
    );
    const offsetSnap = await getDocs(offsetQuery);
    const lastVisible = offsetSnap.docs[offsetSnap.docs.length - 1];

    if (lastVisible) {
      baseQuery = query(
        collection(db, resource),
        orderBy(sortField, direction),
        startAfter(lastVisible),
        limit(perPage)
      );
    }
  }

  return getDocs(baseQuery);
};

// Wrap your dataProvider
export const dataProvider = {
  ...baseDataProvider,
  async getList<RecordType extends RaRecord<Identifier> = any>(
    resource: string,
    params: GetListParams
  ): Promise<GetListResult<RecordType>> {
    return withAdminSessionRetry(async () => {
      console.log(
        `[dataProvider] getList called for resource="${resource}" with params`,
        params
      );

      if (resource === 'channels') {
        return { data: [], total: 0 };
      }

      const { q } = params.filter || {};
      const { page, perPage } = params.pagination;
      const { field: sortField = 'id', order } = params.sort || {};
      const direction = (order?.toLowerCase() as 'asc' | 'desc') || 'asc';

      const pageSnap = await getPagedDocs(
        resource,
        sortField,
        direction,
        page,
        perPage
      );
      let docs = pageSnap.docs;

      if (q && typeof q === 'string') {
        const lowerQ = q.toLowerCase();
        docs = docs.filter((doc) =>
          ((doc.data() as any).email || '').toLowerCase().includes(lowerQ)
        );
      }

      const data = docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as RecordType[];

      const countSnap = await getDocs(
        query(collection(db, resource), orderBy(sortField, direction))
      );
      const total = countSnap.size;

      console.log(
        `[dataProvider] getList returning ${data.length} records for resource="${resource}"`
      );

      return { data, total };
    });
  },
  async getOne(resource: string, params: any) {
    return withAdminSessionRetry(() => baseDataProvider.getOne(resource, params));
  },
  async update(resource: string, params: UpdateParams) {
    try {
      return await withAdminSessionRetry(() =>
        baseDataProvider.update(resource, params)
      );
    } catch (err: any) {
      if (isPermissionDeniedError(err)) {
        console.error(`Cannot update ${resource}/${params.id}:`, err);
        throw new Error('You do not have permission to update this record.');
      }
      throw err;
    }
  },
};

export const authProvider = {
  login: async (params: any) => {
    const result = await baseAuthProvider.login(params);
    await ensureAdminSessionEventually();
    return result;
  },
  logout: baseAuthProvider.logout,
  checkAuth: async (params: any) => {
    await baseAuthProvider.checkAuth(params);
    await ensureAdminSessionEventually();
  },
  checkError: baseAuthProvider.checkError,
  getIdentity: async () => {
    const identity = baseAuthProvider.getIdentity
      ? await baseAuthProvider.getIdentity()
      : null;
    const { user } = await ensureAdminSession({ forceRefresh: false });

    return (
      identity ?? {
        id: user.uid,
        fullName: user.displayName ?? user.email ?? '',
        avatar: user.photoURL ?? '',
      }
    );
  },
  getPermissions: async (_params: any) => {
    const { claims } = await ensureAdminSession({ forceRefresh: false });
    return claims;
  },
};


/**
 * Fetches a user's subcollection (e.g., ad_rewards, withdrawals, payouts) from Firestore.
 * Usage: fetchUserSubcollection(userId, 'ad_rewards')
 */
export const fetchUserSubcollection = async <T = DocumentData>(
  userId: string,
  subcollection: 'ad_rewards' | 'withdrawals' | 'payouts'
): Promise<Array<{ id: string } & T>> => {
  try {
    return await withAdminSessionRetry(async () => {
      const ref = collection(db, 'users', userId, subcollection);
      const snap = await getDocs(ref);
      return snap.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as T),
      }));
    });
  } catch (err) {
    console.error(`Error fetching ${subcollection} for user ${userId}:`, err);
    return [];
  }
};

export const refreshUserClaims = async () => {
  if (!auth.currentUser) {
    return null;
  }

  const { claims } = await ensureAdminSession({ forceRefresh: true });
  return claims;
};
